import { ReactNode, useEffect, useState } from "react";
import { matchPath, useLocation, useOutlet } from "react-router-dom";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import FullScreenSheet from "@/components/layout/FullScreenSheet";

// Overlay routes: other users' profiles and single conversations. Excludes
// /profile (self, no param match) and /profile/edit (userId === "edit", same
// trick as HeaderWithMenubar).
export function isOverlayPath(pathname: string): boolean {
  const profileMatch = matchPath("/profile/:userId", pathname);
  if (profileMatch && profileMatch.params.userId !== "edit") return true;
  return matchPath("/messages/:profileId", pathname) !== null;
}

type OverlayEntry = {
  key: string; // location.key — unique per history entry
  idx: number; // history index at push time (window.history.state.idx)
  panel: ReactNode; // frozen outlet snapshot
  open: boolean; // false while the exit animation plays, pruned right after
};

const EXIT_ANIMATION_MS = 350;

function historyIdx(): number {
  return (window.history.state as { idx?: number } | null)?.idx ?? 0;
}

/**
 * Desktop: renders the outlet as a normal routed page, exactly as before.
 *
 * Mobile: overlay routes (profile view, conversation) render as full-screen
 * sheets that slide in over a frozen snapshot of the last non-overlay page,
 * so the underlying list keeps its scroll position, queries and state. Each
 * overlay history entry gets its own sheet, so pushing profile → conversation
 * → profile stacks sheets like iOS push navigation and the browser history
 * drives them; every existing navigate() call and back button keeps working.
 * Frozen outlet elements retain their baked-in route params, so useParams
 * keeps working inside snapshots after the live location has moved on.
 */
export default function MobileOverlayOutlet() {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const location = useLocation();
  const outlet = useOutlet();
  const isOverlay = isOverlayPath(location.pathname);

  const [base, setBase] = useState<{ path: string | null; panel: ReactNode }>(
    () => ({
      path: isOverlay ? null : location.pathname,
      panel: isOverlay ? null : outlet,
    }),
  );

  const [stack, setStack] = useState<OverlayEntry[]>(() =>
    isOverlay
      ? [{ key: location.key, idx: historyIdx(), panel: outlet, open: true }]
      : [],
  );

  // Guarded render-phase captures (React restarts the render before
  // committing, so nothing flashes). Runs on desktop too, keeping the base
  // fresh in case the viewport shrinks below the breakpoint mid-session.
  if (!isOverlay && base.path !== location.pathname) {
    setBase({ path: location.pathname, panel: outlet });
  }
  if (isOverlay) {
    const active = stack.filter((e) => e.open);
    const top = active[active.length - 1];
    if (top?.key !== location.key) {
      const i = stack.findIndex((e) => e.key === location.key);
      if (i >= 0) {
        // Went back (or forward) to an overlay already in the stack: close
        // everything above it and make sure it is open itself.
        setStack(
          stack.map((e, j) =>
            j > i ? { ...e, open: false } : j === i ? { ...e, open: true } : e,
          ),
        );
      } else {
        // Pushed a new overlay: close forward-orphans, slide the new one in.
        const idx = historyIdx();
        setStack([
          ...stack.map((e) => (e.idx >= idx ? { ...e, open: false } : e)),
          { key: location.key, idx, panel: outlet, open: true },
        ]);
      }
    }
  } else if (stack.some((e) => e.open)) {
    // Navigated to a base route: close every sheet.
    setStack(stack.map((e) => ({ ...e, open: false })));
  }

  // Unmount closed sheets once their exit animation has played.
  const hasClosedEntries = stack.some((e) => !e.open);
  useEffect(() => {
    if (!hasClosedEntries) return;
    const timer = setTimeout(
      () => setStack((s) => s.filter((e) => e.open)),
      EXIT_ANIMATION_MS,
    );
    return () => clearTimeout(timer);
  }, [hasClosedEntries, stack]);

  // The list pages save window.scrollY to sessionStorage[pathname] on card
  // click and consume it on remount. Under the freeze the base never
  // remounts, so consume the stale marker ourselves — otherwise a later data
  // refetch would scroll-jump the list back to the old click position.
  useEffect(() => {
    if (!isDesktop && isOverlay && base.path) {
      sessionStorage.removeItem(base.path);
    }
  }, [isDesktop, isOverlay, base.path]);

  // Replaces react-remove-scroll's body lock (Radix) WITHOUT
  // overscroll-behavior, which would re-suppress the browser's swipe-back
  // navigation gesture. Plain overflow:hidden is not enough here: the frozen
  // base keeps the document tall, and when the iOS keyboard opens WebKit
  // auto-scrolls the document to the focused input regardless of body
  // overflow (dragging the fixed sheet along with it, since iOS anchors
  // fixed elements to the document while the keyboard is up). Fixing the
  // body at -scrollY collapses the document's scrollable height so there is
  // nothing to scroll, while keeping the base visually in place.
  const anyOpen = stack.some((e) => e.open);
  useEffect(() => {
    if (isDesktop || !anyOpen) return;
    const { style } = document.body;
    const scrollY = window.scrollY;
    const prev = {
      position: style.position,
      top: style.top,
      left: style.left,
      right: style.right,
      overflow: style.overflow,
    };
    style.position = "fixed";
    style.top = `-${scrollY}px`;
    style.left = "0";
    style.right = "0";
    style.overflow = "hidden";
    return () => {
      style.position = prev.position;
      style.top = prev.top;
      style.left = prev.left;
      style.right = prev.right;
      style.overflow = prev.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [isDesktop, anyOpen]);

  if (isDesktop) return outlet;

  return (
    <>
      {base.panel}
      {stack.map((entry) => (
        <FullScreenSheet key={entry.key} open={entry.open}>
          <div className="flex-1 overflow-y-auto overscroll-y-contain pt-safe-top">
            <div className="flex w-full min-h-full py-4 px-2 sm:px-6">
              {entry.panel}
            </div>
          </div>
        </FullScreenSheet>
      ))}
    </>
  );
}
